import { t } from '@web/i18n';
import { AppIcons, Kbd } from '@web/ui/core/atoms';
import { useCommandPalette } from '@web/data/repositories';

/**
 * Apple platform (macOS/iOS) → the shortcut renders as ⌘K; elsewhere Ctrl K.
 * `useMetaKeyShortcut` already accepts metaKey OR ctrlKey — only the label
 * varies. UA-based (navigator.platform is deprecated); modern iPads report
 * "Macintosh", which still maps to ⌘K correctly.
 */
export function isApplePlatform(userAgent: string): boolean {
  return /Mac|iPhone|iPad/.test(userAgent);
}

const IS_APPLE = isApplePlatform(typeof navigator === 'undefined' ? '' : navigator.userAgent);

/**
 * SidebarSearchButton — a pill-shaped search trigger placed at the top of
 * the sidebar (above the nav body). Clicking it opens the command palette.
 * The ⌘K / Ctrl K badge is a visual reminder of the keyboard shortcut.
 */
export function SidebarSearchButton(): React.JSX.Element {
  const setOpen = useCommandPalette((s) => s.setOpen);

  return (
    <button
      type="button"
      onClick={() => {
        setOpen(true);
      }}
      className="flex w-full items-center gap-2 rounded-md border border-border bg-card px-2.5 py-1.5 text-left text-meta text-muted-foreground transition-colors duration-fast ease-standard hover:bg-accent hover:text-foreground-default"
      aria-label={t('search.openLabel')}
    >
      <AppIcons name="magnifying-glass" size={14} className="flex-shrink-0" />
      <span className="flex-1">{t('search.openLabel')}</span>
      <Kbd>{t(IS_APPLE ? 'search.shortcutMac' : 'search.shortcutOther')}</Kbd>
    </button>
  );
}
